// components/(postTypes)/symbols/single/coin/layout/SinglePrice.tsx
'use client';

import React from 'react';
import { formatNumber } from '@/utils/coins/formatNumber';
import ChangeValue from '@/components/(postTypes)/symbols/ChangeValue';
import { useCoinPriceSocket } from '@/hooks/useCoinPriceSocket';
import { formatTomanPrice } from '@/utils/coins/bubble';
import { isUsdQuoted } from '@/utils/coins/quote';
import { toPersianNumber } from '@/utils/persianUtils';

type TimePeriod = '1h' | '24h' | '7d' | '30d' | '1y' | '5y';

type Props = {
    symbol: string;
    isUSDT: boolean;
    price_buy: number; // USD
    ir_price_buy: number; // Toman
    typeId?: number;
    asset_type?: string;
    timePeriod: string;
    timePeriodTranslations: Record<TimePeriod, string>;
    change: number;
    /** 24h change in toman (gold & currency) */
    changeToman?: number;
    isCurrencyOrGold?: boolean;
    symbolType?: 'crypto' | 'coin' | 'gold' | 'currency';
    /** Gold/currency quoted in dollars (the world gold ounce): shown like a coin, dollars first */
    priceUsd?: number;
    quoteCurrency?: string;
};

const SinglePrice: React.FC<Props> = ({
    symbol,
    isUSDT,
    price_buy,
    ir_price_buy,
    typeId = 1,
    asset_type,
    change,
    changeToman,
    isCurrencyOrGold = false,
    symbolType = 'crypto',
    priceUsd,
    quoteCurrency,
}) => {
    const usdQuoted =
        isCurrencyOrGold && isUsdQuoted({ price_usd: priceUsd, quote_currency: quoteCurrency });
    const {
        livePriceToman,
        livePriceUsdt,
        priceTomanChangeClass,
        priceUsdtChangeClass,
        liveChange24h,
        liveChangeToman,
    } = useCoinPriceSocket(symbol, usdQuoted ? priceUsd || 0 : price_buy, ir_price_buy, symbolType);

    const isStableCoin = asset_type === 'stableCoin';

    // ==========================================
    // RENDER: Dollar-quoted gold (the world gold ounce): dollars big, toman + its change under it
    // ==========================================
    if (usdQuoted) {
        const tomanChange = liveChangeToman ?? changeToman ?? 0;
        return (
            <div className='flex flex-col items-end gap-0.5 dir-rtl overflow-x-clip'>
                <div className='flex items-center gap-2.5 sm:gap-3.5'>
                    {/* 24h Change Pill Badge & Label */}
                    <div className='flex flex-col items-center justify-center shrink-0 gap-0.5'>
                        <span className='text-[10px] text-gray-400 dark:text-gray-500 font-medium whitespace-nowrap'>
                            ۲۴ ساعت
                        </span>
                        <ChangeValue change={liveChange24h ?? (change || 0)} />
                    </div>

                    {/* Primary USD Price Big Number */}
                    <div className='flex items-center gap-1.5 relative'>
                        <div
                            className={`font-mono max-sm:text-2xl max-xl:text-3xl text-4xl font-extrabold transition-all duration-700 ${
                                priceUsdtChangeClass ?? 'text-gray-900 dark:text-white'
                            }`}
                            dir='ltr'
                        >
                            <span className='text-lg font-bold text-gray-400 dark:text-gray-500 mr-0.5'>
                                $
                            </span>
                            {toPersianNumber(
                                formatNumber(
                                    livePriceUsdt || priceUsd || 0,
                                    false,
                                    true,
                                    'USD',
                                    true,
                                ),
                            )}
                        </div>
                        <span className='absolute -top-2 left-0 flex size-2.5 shrink-0'>
                            <span className='absolute inline-flex size-full animate-ping rounded-full opacity-75 bg-emerald-500'></span>
                            <span className='relative inline-flex size-2.5 rounded-full bg-emerald-500'></span>
                        </span>
                    </div>
                </div>

                {/* Toman Equivalence Subtitle + its 24h change */}
                <div className='flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 font-yekanBakh dir-rtl pl-1'>
                    <span
                        className={`transition-colors duration-700 ${priceTomanChangeClass ?? ''}`}
                    >
                        <bdi dir='ltr'>
                            {formatTomanPrice(livePriceToman || ir_price_buy || 0, symbol)}
                        </bdi>{' '}
                        تومان
                    </span>
                    {Boolean(tomanChange) && (
                        <span
                            className={`text-[11px] whitespace-nowrap ${
                                tomanChange > 0
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : 'text-rose-600 dark:text-rose-400'
                            }`}
                        >
                            <bdi dir='ltr'>
                                {tomanChange > 0 ? '+' : '-'}
                                {formatNumber(Math.abs(tomanChange), false, true, 'TMN', true)}
                            </bdi>
                        </span>
                    )}
                </div>
            </div>
        );
    }

    // ==========================================
    // RENDER: USDT / StableCoin / Gold / Currency Toman-only Layout
    // ==========================================
    if (isUSDT || isStableCoin || isCurrencyOrGold) {
        return (
            <div className='flex flex-col items-end gap-0.5 dir-rtl overflow-x-clip'>
                {/* Mobile: change sits under the price so long toman prices fit next to the title */}
                <div className='flex flex-col-reverse items-end gap-0.5 sm:flex-row sm:items-center sm:gap-3.5'>
                    {/* 24h Change Pill Badge & Label */}
                    <div className='flex sm:flex-col items-center justify-center shrink-0 gap-1 sm:gap-0.5'>
                        <span className='text-[10px] text-gray-400 dark:text-gray-500 font-medium whitespace-nowrap'>
                            ۲۴ ساعت
                        </span>
                        <div className='flex items-center gap-1.5'>
                            <ChangeValue change={liveChange24h ?? (change || 0)} />
                            {/* Desktop: toman change, small, next to the percentage (mobile shows it in the market box) */}
                            {isCurrencyOrGold && Boolean(liveChangeToman ?? changeToman) && (
                                <span
                                    className={`hidden lg:inline text-[11px] font-medium whitespace-nowrap ${
                                        (liveChangeToman ?? changeToman ?? 0) > 0
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : 'text-rose-600 dark:text-rose-400'
                                    }`}
                                >
                                    <bdi dir='ltr'>
                                        {(liveChangeToman ?? changeToman ?? 0) > 0 ? '+' : '-'}
                                        {formatNumber(
                                            Math.abs(liveChangeToman ?? changeToman ?? 0),
                                            false,
                                            true,
                                            'TMN',
                                            true,
                                        )}
                                    </bdi>{' '}
                                    تومان
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Primary Toman Price Big Number */}
                    <div className='flex items-center gap-1.5 relative'>
                        <div
                            className={`font-yekanBakh max-sm:text-xl max-xl:text-3xl text-4xl font-extrabold transition-all duration-700 ${
                                priceTomanChangeClass ?? 'text-gray-900 dark:text-white'
                            }`}
                        >
                            <bdi dir='ltr'>
                                {formatTomanPrice(livePriceToman || ir_price_buy || 0, symbol)}
                            </bdi>
                            <span className='text-xs sm:text-sm font-normal mr-1.5 text-gray-500 dark:text-gray-400'>
                                تومان
                            </span>
                        </div>
                        <span className='absolute -top-2 left-0 flex size-2.5 shrink-0'>
                            <span
                                className={`absolute inline-flex size-full animate-ping rounded-full opacity-75 bg-emerald-500`}
                            ></span>
                            <span
                                className={`relative inline-flex size-2.5 rounded-full bg-emerald-500`}
                            ></span>
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    // ==========================================
    // RENDER: Generic Crypto Coin Layout
    // ==========================================
    return (
        <div className='flex flex-col items-end gap-0.5 dir-rtl overflow-x-clip'>
            <div className='flex items-center gap-2.5 sm:gap-3.5'>
                {/* 24h Change Pill Badge & Label */}
                <div className='flex flex-col items-center justify-center shrink-0 gap-0.5'>
                    <span className='text-[10px] text-gray-400 dark:text-gray-500 font-medium whitespace-nowrap'>
                        ۲۴ ساعت
                    </span>
                    <ChangeValue change={change || 0} />
                </div>

                {/* Primary USD Price Big Number */}
                <div className='flex items-center gap-1.5 relative'>
                    <div
                        className={`font-mono max-sm:text-2xl max-xl:text-3xl text-4xl font-extrabold transition-all duration-700 ${
                            priceUsdtChangeClass ?? 'text-gray-900 dark:text-white'
                        }`}
                        dir='ltr'
                    >
                        <span className='text-lg font-bold text-gray-400 dark:text-gray-500 mr-0.5'>
                            $
                        </span>
                        {toPersianNumber(
                            formatNumber(livePriceUsdt || 0, false, true, 'USD', true),
                        )}
                    </div>
                    <span className='absolute -top-2 left-0 flex size-2.5 shrink-0'>
                        <span
                            className={`absolute inline-flex size-full animate-ping rounded-full opacity-75 bg-emerald-500`}
                        ></span>
                        <span
                            className={`relative inline-flex size-2.5 rounded-full bg-emerald-500`}
                        ></span>
                    </span>
                </div>
            </div>

            {/* Toman Equivalence Subtitle */}
            {typeId !== 2 && (
                <div className='text-xs font-medium text-gray-500 dark:text-gray-400 font-yekanBakh dir-rtl pl-1'>
                    <span
                        className={`transition-colors duration-700 ${priceTomanChangeClass ?? ''}`}
                    >
                        {formatNumber(livePriceToman || 0, false, true, 'TMN', true)} تومان
                    </span>
                </div>
            )}
        </div>
    );
};

export default SinglePrice;
